import React, { useMemo, useState } from "react";
import { Iconography, IconographySizes } from "@hid-galaxy-ui/galaxy-react";
import MetricTrendCard from "./MetricTrendCard";
import {
  ECOSYSTEM_TAB_ICONS,
  GlobeSummaryIcon,
  PeopleSummaryIcon,
} from "./EcosystemTabIcons";
import { MobilePhoneTabIcon } from "../project-status/FilterTabIcons";
import { EndCustomerEcosystemData } from "../../types";

interface EndCustomerEcosystemSectionProps {
  data: EndCustomerEcosystemData;
  showInlineMetrics?: boolean;
  isStaticDisabled?: boolean;
}

const EndCustomerEcosystemSection: React.FC<EndCustomerEcosystemSectionProps> = ({
  data,
  showInlineMetrics = true,
  isStaticDisabled = false,
}) => {
  const [activeTab, setActiveTab] = useState(data.technologyTabs[0] ?? "");
  const [selectedCustomerId, setSelectedCustomerId] = useState(
    data.customers[0]?.id ?? ""
  );

  const selectedCustomer = useMemo(
    () => data.customers.find((customer) => customer.id === selectedCustomerId),
    [data.customers, selectedCustomerId]
  );

  return (
    <div
      className={`end-customer-ecosystem${
        isStaticDisabled ? " end-customer-ecosystem--static-disabled" : ""
      }`}
    >
      <div className="end-customer-ecosystem__intro">
        <div className="end-customer-ecosystem__header">
          <div className="end-customer-ecosystem__header-text">
            <h3 className="end-customer-ecosystem__title">End-Customer Ecosystem</h3>
            <p className="end-customer-ecosystem__subtitle">{data.subtitle}</p>
          </div>

          <div className="end-customer-ecosystem__tabs">
            {data.technologyTabs.map((tab) => {
              const TabIcon = ECOSYSTEM_TAB_ICONS[tab] ?? MobilePhoneTabIcon;

              return (
                <button
                  key={tab}
                  type="button"
                  className={`end-customer-ecosystem__tab ${
                    activeTab === tab ? "end-customer-ecosystem__tab--active" : ""
                  }`}
                  onClick={() => {
                    if (!isStaticDisabled) {
                      setActiveTab(tab);
                    }
                  }}
                  disabled={isStaticDisabled}
                >
                  <TabIcon className="end-customer-ecosystem__tab-icon" />
                  <span className="end-customer-ecosystem__tab-label">{tab}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="end-customer-ecosystem__summary-row">
        <div className="end-customer-ecosystem__summary-card">
          <div className="end-customer-ecosystem__summary-card-top">
            <span className="end-customer-ecosystem__summary-label">Total End Customers</span>
            <PeopleSummaryIcon className="end-customer-ecosystem__summary-icon end-customer-ecosystem__summary-icon--blue" />
          </div>
          <span className="end-customer-ecosystem__summary-value">{data.totalEndCustomers}</span>
        </div>

        <div className="end-customer-ecosystem__summary-card">
          <div className="end-customer-ecosystem__summary-card-top">
            <span className="end-customer-ecosystem__summary-label">Active Regions</span>
            <GlobeSummaryIcon className="end-customer-ecosystem__summary-icon end-customer-ecosystem__summary-icon--green" />
          </div>
          <div className="end-customer-ecosystem__region-pills">
            {data.activeRegions.length > 0 ? (
              data.activeRegions.map((region) => (
                <span key={region} className="end-customer-ecosystem__region-pill">
                  {region}
                </span>
              ))
            ) : (
              <span className="end-customer-ecosystem__region-pill end-customer-ecosystem__region-pill--empty">
                0
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="end-customer-ecosystem__main">
        <div className="end-customer-ecosystem__left">
          <label className="end-customer-ecosystem__select-label" htmlFor="end-customer-select">
            Select End Customer
          </label>
          <div className="end-customer-ecosystem__select-wrap">
            <select
              id="end-customer-select"
              className="end-customer-ecosystem__select"
              value={selectedCustomerId}
              onChange={(event) => setSelectedCustomerId(event.target.value)}
              disabled={isStaticDisabled}
            >
              {data.customers.map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.name}
                </option>
              ))}
            </select>
            <Iconography icon="chevronDown" size={IconographySizes.Small} />
          </div>

          {selectedCustomer && (
            <>
              <div className="end-customer-ecosystem__customer-card">
                <div className="end-customer-ecosystem__customer-logo">
                  <Iconography icon="building" size={IconographySizes.Medium} />
                </div>
                <div className="end-customer-ecosystem__customer-info">
                  <h4>{selectedCustomer.name}</h4>
                  <p>{selectedCustomer.customerId}</p>
                </div>
              </div>

              <span className="end-customer-ecosystem__customer-region">
                {selectedCustomer.region}
              </span>

              <div className="end-customer-ecosystem__customer-stats">
                <span className="end-customer-ecosystem__customer-stat">
                  License: {selectedCustomer.licenses}
                </span>
                <span className="end-customer-ecosystem__customer-stat">
                  Users: {selectedCustomer.users}
                </span>
              </div>
            </>
          )}
        </div>

        {showInlineMetrics && (
          <div className="end-customer-ecosystem__metrics">
            {data.metrics.map((metric) => (
              <MetricTrendCard
                key={metric.id}
                data={metric}
                isStaticDisabled={isStaticDisabled}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EndCustomerEcosystemSection;
